// Export filenames. The wizard previews the exact name the pipeline will write, so the
// *format* here is mirrored by js-geometry-tools'
// `js_geometry_tools_entrypoints/configuration/ifc_export_naming.py`. Any change to the segments,
// their order, the sanitizing, the tokens or the truncation has to land there too, or the preview
// stops matching the downloaded file. The two files do not have identical function lists — the
// pipeline needs a tolerance default this side never does, because the wizard always picks one.
//
//   ifc: [ProjectName]_[ExportType]_[ScanScope]_[Area]_[DeviationTolerance]_[Trades].ifc
//   zip: [ProjectName]_[ExportType]_ifc-batch.zip
//
// The pipeline resolves ProjectName and Area from the gateway itself (project name / floor number),
// so only the tokens it can't derive — the tolerance and trade labels — travel with the export
// request.

import type { DeviationsIfcType } from "../../../components/dialogs/export_deviations_ifc_dialog";

/** Characters no filesystem we export to accepts; dropped rather than substituted. */
const INVALID_FILE_NAME_CHARACTERS = /[/\\:*?"<>|]/g;

/** Filesystem limit for a single filename, extension included. */
export const MAX_FILE_NAME_LENGTH = 255;

/** Marker appended in place of the tail of an over-long filename. */
const FILE_NAME_TRUNCATION_MARKER = "...";

/** Trade token standing in for the whole trade list, rather than naming every trade. */
export const ALL_TRADES_FILE_NAME_TOKEN = "AllTrades";

/** Used when a project has no name, so the filename never starts with a stray separator. */
const FALLBACK_PROJECT_FILE_NAME = "export";

/** `ExportType` token per export mode. */
const EXPORT_TYPE_FILE_NAME_TOKENS: Record<string, string> = {
  deviations: "Deviation",
  all_deviations: "Deviation",
  current_deviations: "Deviation",
  as_built: "As-Built",
  not_built: "Not Built",
};

/** Fallback for a mode this module has not been taught yet; matches the Python mirror. */
const DEFAULT_EXPORT_TYPE_TOKEN = "Deviation";

/** Scan scope that names only the newest capture, as opposed to every scan to date. */
const MOST_RECENT_SCAN_MODE: DeviationsIfcType = "current_deviations";

/**
 * Filename-safe form of a single filename segment: ` - ` collapses to `-` so a floor like
 * `C1 - Level 1` reads as `C1-Level1`, filesystem-invalid characters are dropped, and remaining
 * whitespace is removed so spaces can never be confused with the `_` that separates segments.
 */
export const sanitizeFileNameSegment = (value: string | null | undefined): string =>
  (value ?? "")
    .replace(/\s*-\s*/g, "-")
    .replace(INVALID_FILE_NAME_CHARACTERS, "")
    .replace(/\s+/g, "");

/**
 * `ExportType` token for an export mode, e.g. `Deviation`. An unrecognised mode falls back to the
 * deviation label rather than to the mode string itself: a raw mode would put an unvetted value in
 * a filename, and the Python mirror resolves the same way, so a new mode cannot make the preview
 * and the written file disagree.
 */
export const exportTypeFileNameToken = (mode: string): string =>
  EXPORT_TYPE_FILE_NAME_TOKENS[mode] ?? DEFAULT_EXPORT_TYPE_TOKEN;

/** `ScanScope` token for an export mode: `MostRecentScan` for the newest scan, else `AllScans`. */
export const scanScopeFileNameToken = (mode: string): string =>
  mode === MOST_RECENT_SCAN_MODE ? "MostRecentScan" : "AllScans";

/**
 * `Trades` segment: a whole-project selection collapses to a single `AllTrades` token instead of
 * naming every trade, and anything narrower lists the selected names joined by `_`. The names are
 * expected to be already collapsed to their parent groups where a group is fully selected.
 */
export const tradesFileNameSegment = (tradeNames: string[], allTradesSelected: boolean): string => {
  if (allTradesSelected) {
    return ALL_TRADES_FILE_NAME_TOKEN;
  }
  return tradeNames.map(sanitizeFileNameSegment).filter(Boolean).join("_");
};

/**
 * Truncates a filename to the filesystem limit, marking the cut with `...` so a user can tell the
 * name was shortened. Truncation is applied to the assembled name rather than to any one segment,
 * so the leading segments — the ones that identify the export — always survive.
 */
const withExtension = (baseName: string, extension: string): string => {
  if (baseName.length + extension.length <= MAX_FILE_NAME_LENGTH) {
    return baseName + extension;
  }
  const keep = MAX_FILE_NAME_LENGTH - extension.length - FILE_NAME_TRUNCATION_MARKER.length;
  return baseName.slice(0, keep) + FILE_NAME_TRUNCATION_MARKER + extension;
};

/**
 * The filename tokens the export request has to carry to the pipeline. Everything else in the name
 * the pipeline derives itself, either from the export mode or from the project/floor records.
 */
export type IfcExportFileNamingLabels = {
  /** Tolerance token, e.g. `3in`; empty for exports with no tolerance. */
  toleranceLabel: string
  /** `Trades` segment, e.g. `AllTrades` or `Mechanical_Electrical`; empty when no trade filter applies. */
  tradesLabel: string
};

export type IfcFileNameParams = {
  projectName: string | null | undefined
  /** `ExportType` token, from `exportTypeFileNameToken`. */
  exportType: string
  /** `ScanScope` token, from `scanScopeFileNameToken`. */
  scanScope: string
  /** The exported floor/area, e.g. a floor number. */
  area: string | null | undefined
  /** Tolerance token, e.g. `3in`; omitted for exports that have no tolerance. */
  deviationTolerance?: string | null
  /** `Trades` segment, from `tradesFileNameSegment`; omitted when no trade filter applies. */
  trades?: string | null
};

/**
 * The name of one exported .ifc file. Every segment is sanitized on the way in — including the
 * export-type and trade tokens, which are already filename-safe — so no caller can slip a space or
 * a path separator into a name, and so `_` unambiguously separates segments.
 */
export const generateIfcFileName = (params: IfcFileNameParams): string => {
  const { projectName, exportType, scanScope, area, deviationTolerance, trades } = params;
  const baseName = [
    sanitizeFileNameSegment(projectName) || FALLBACK_PROJECT_FILE_NAME,
    sanitizeFileNameSegment(exportType),
    sanitizeFileNameSegment(scanScope),
    sanitizeFileNameSegment(area),
    sanitizeFileNameSegment(deviationTolerance),
    sanitizeFileNameSegment(trades),
  ].filter(Boolean).join("_");
  return withExtension(baseName, ".ifc");
};

export type ZipFileNameParams = {
  projectName: string | null | undefined
  /** `ExportType` token, from `exportTypeFileNameToken`. */
  exportType: string
};

/** The name of the zip bundling a multi-area export; deliberately carries no area/tolerance/trades. */
export const generateZipFileName = ({ projectName, exportType }: ZipFileNameParams): string => {
  const baseName = [
    sanitizeFileNameSegment(projectName) || FALLBACK_PROJECT_FILE_NAME,
    sanitizeFileNameSegment(exportType),
    "ifc-batch",
  ].filter(Boolean).join("_");
  return withExtension(baseName, ".zip");
};
